import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import Chip from "@mui/material/Chip";
import CircularProgress from "@mui/material/CircularProgress";
import Grid from "@mui/material/Grid";
import Tooltip from "@mui/material/Tooltip";
import { useEffect, useState } from "react";
import { PersonSkill } from "../../api";
import { getAdditionalPersonSkillsById } from "../../api/person/additional-skills";
import { ProfileSection } from "./profile-section";

interface PersonSkillsProps {
	personSkills: PersonSkill[];
	personId: number;
	assignmentId?: number;
	/** Prefetched additional skills — when provided, skips the self-fetch. */
	prefetchedAdditionalSkills?: string[];
}

export const PersonSkills = ({
	personSkills,
	personId,
	assignmentId,
	prefetchedAdditionalSkills,
}: PersonSkillsProps) => {
	const hasPrefetched = prefetchedAdditionalSkills !== undefined;
	const [additionalSkills, setAdditionalSkills] = useState<string[]>(
		hasPrefetched ? prefetchedAdditionalSkills : []
	);
	const [isLoading, setIsLoading] = useState(!hasPrefetched);

	// Lazy load additional skills — only when not prefetched
	useEffect(() => {
		if (hasPrefetched) return;
		if (!personId || personId <= 0) {
			setIsLoading(false);
			return;
		}

		setIsLoading(true);
		setAdditionalSkills([]);

		getAdditionalPersonSkillsById(personId, assignmentId)
			.then((res) => {
				const skills = res.results?.[0]?.additional_skills ?? [];
				setAdditionalSkills(skills);
			})
			.catch((error) => {
				console.error("Failed to load additional skills:", error);
			})
			.finally(() => setIsLoading(false));
	}, [personId, assignmentId, hasPrefetched]);

	// Sync from prefetched data when person changes
	useEffect(() => {
		if (hasPrefetched) {
			setAdditionalSkills(prefetchedAdditionalSkills);
			setIsLoading(false);
		}
	}, [prefetchedAdditionalSkills, hasPrefetched]);

	return (
		<ProfileSection title="Skills" dataComponent="person-skills-paper">
			<Grid container direction="row" spacing={1}>
				{personSkills?.map((skill) => (
					<Grid key={skill.skill_name}>
						<Chip
							size="small"
							label={
								<span
									dangerouslySetInnerHTML={{
										__html: skill.name,
									}}
								/>
							}
							data-component="person-skill-chip"
						/>
					</Grid>
				))}
				{isLoading ? (
					<Grid>
						<Tooltip title="Loading AI-extracted skills..." arrow>
							<Chip
								size="small"
								icon={<CircularProgress size={12} />}
								label="..."
								sx={{ opacity: 0.6 }}
							/>
						</Tooltip>
					</Grid>
				) : (
					additionalSkills.map((skill) => (
						<Grid key={`ai-${skill}`}>
							<Tooltip title="AI-extracted from profile text" arrow>
								<Chip
									size="small"
									icon={<AutoAwesomeIcon sx={{ fontSize: 14 }} />}
									label={
										<span
											dangerouslySetInnerHTML={{
												__html: skill,
											}}
										/>
									}
									sx={{
										"& .MuiChip-icon": {
											color: "inherit",
											opacity: 0.7,
										},
									}}
									data-component="ai-skill-chip"
								/>
							</Tooltip>
						</Grid>
					))
				)}
			</Grid>
		</ProfileSection>
	);
};
